<script setup>
// Página de criação/edição de campo customizado.
// Usa SettingsLayout + Breadcrumb (padrão visual da plataforma - mesmo padrão
// de Assignment Policy / Agent Capacity).
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { convertToAttributeSlug } from 'dashboard/helper/commons.js';
import { ATTRIBUTE_MODELS } from './constants';
import SettingsLayout from '../SettingsLayout.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  attributeId: { type: Number, default: null },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const mode = computed(() => (props.attributeId ? 'edit' : 'create'));
const isSubmitting = ref(false);

// Tabs - separa o form em 3 fases coerentes pra não inundar o usuário.
// Antes era um único scroll vertical gigante com Identificação/Tipo/Validação/API
// empilhados - sem hierarquia visual e cansativo.
const activeTab = ref('identity');
const FORM_TABS = computed(() => {
  const tabs = [
    { key: 'identity',   label: 'Identificação', icon: 'i-lucide-tag' },
    { key: 'type',       label: 'Tipo & Validação', icon: 'i-lucide-shapes' },
  ];
  // Aba "Integração" só aparece quando o tipo escolhido permite (text com validação API, ou list)
  // - controlada via v-show no template; mantemos o tab sempre disponível.
  tabs.push({ key: 'api', label: 'Integração', icon: 'i-lucide-plug' });
  return tabs;
});

const TYPE_CARDS = [
  {
    id: 0,
    key: 'text',
    icon: 'i-lucide-type',
    label: 'Texto',
    hint: 'Texto livre',
  },
  {
    id: 1,
    key: 'number',
    icon: 'i-lucide-hash',
    label: 'Número',
    hint: 'Inteiro/decimal',
  },
  {
    id: 2,
    key: 'currency',
    icon: 'i-lucide-banknote',
    label: 'Moeda',
    hint: 'R$ 0,00',
  },
  {
    id: 3,
    key: 'percent',
    icon: 'i-lucide-percent',
    label: 'Percentual',
    hint: '0-100%',
  },
  {
    id: 4,
    key: 'link',
    icon: 'i-lucide-link',
    label: 'Link',
    hint: 'URL https://',
  },
  {
    id: 5,
    key: 'date',
    icon: 'i-lucide-calendar',
    label: 'Data',
    hint: 'dd/mm/aaaa',
  },
  {
    id: 6,
    key: 'list',
    icon: 'i-lucide-list',
    label: 'Lista',
    hint: 'Seleção de opções',
  },
  {
    id: 7,
    key: 'checkbox',
    icon: 'i-lucide-check-square',
    label: 'Checkbox',
    hint: 'Sim/Não',
  },
];

const API_VALIDATABLE_TYPES = [0, 1, 2, 3, 4, 5];

const form = reactive({
  displayName: '',
  description: '',
  attributeKey: '',
  attributeModel: 0,
  attributeType: 0,
  values: [],
  regexEnabled: false,
  regexPattern: '',
  regexCue: '',
  isRequired: false,
  minValue: null,
  maxValue: null,
  minLength: null,
  maxLength: null,
  apiValidatorEnabled: false,
  apiValidatorUrl: '',
  externalListEnabled: false,
  externalListUrl: '',
  externalListLabelKey: 'label',
  externalListValueKey: 'value',
  externalListCacheTtl: 300,
  apiSearchable: false,
  apiAuthType: 'none',
  apiAuthToken: '',
  externalListHeadersRaw: '',
});

const isText = computed(() => form.attributeType === 0);
const isNumber = computed(() => [1, 2, 3].includes(form.attributeType));
const isList = computed(() => form.attributeType === 6);
const isCheckbox = computed(() => form.attributeType === 7);
const canValidateViaApi = computed(() =>
  API_VALIDATABLE_TYPES.includes(form.attributeType)
);
const usesApi = computed(
  () =>
    (canValidateViaApi.value && form.apiValidatorEnabled) ||
    (isList.value && form.externalListEnabled)
);

const breadcrumbItems = computed(() => [
  {
    label: t('ATTRIBUTES_MGMT.HEADER'),
    routeName: 'attributes_list',
  },
  {
    label:
      mode.value === 'edit'
        ? t('ATTRIBUTES_MGMT.EDIT.TITLE')
        : t('ATTRIBUTES_MGMT.ADD.TITLE'),
  },
]);

const handleBreadcrumbClick = item => {
  if (item.routeName) {
    router.push({ name: item.routeName, params: route.params });
  }
};

const modelOptions = computed(() =>
  ATTRIBUTE_MODELS.map(m => ({
    id: m.id,
    key: m.key,
    label: t(`ATTRIBUTES_MGMT.ATTRIBUTE_MODELS.${m.key}`),
    // TODO i18n: chave dedicada para a descrição do RadioCard
    description:
      m.id === 0
        ? t('ATTRIBUTES_MGMT.FORM_DIALOG.LOCATION_CONVERSATION')
        : t('ATTRIBUTES_MGMT.FORM_DIALOG.LOCATION_CONTACT'),
  }))
);

const hydrateFromStore = () => {
  if (mode.value !== 'edit') {
    const tabModel = Number(route.query.model || 0);
    form.attributeModel = tabModel === 1 ? 1 : 0;
    return;
  }
  const list = store.getters['attributes/getAttributes'] || [];
  const attr = list.find(a => a.id === props.attributeId);
  if (!attr) return;
  form.displayName = attr.attribute_display_name || '';
  form.description = attr.attribute_description || '';
  form.attributeKey = attr.attribute_key || '';
  form.attributeModel = attr.attribute_model === 'contact_attribute' ? 1 : 0;
  form.attributeType = TYPE_CARDS.findIndex(
    c => c.key === attr.attribute_display_type
  );
  form.values = Array.isArray(attr.attribute_values)
    ? attr.attribute_values
    : [];
  form.regexEnabled = !!attr.regex_pattern;
  form.regexPattern = attr.regex_pattern || '';
  form.regexCue = attr.regex_cue || '';
  form.isRequired = !!attr.is_required;
  form.minValue = attr.min_value;
  form.maxValue = attr.max_value;
  form.minLength = attr.min_length;
  form.maxLength = attr.max_length;
  form.apiValidatorEnabled = !!attr.api_validator_url;
  form.apiValidatorUrl = attr.api_validator_url || '';
  form.externalListEnabled = !!attr.external_list_url;
  form.externalListUrl = attr.external_list_url || '';
  form.externalListLabelKey = attr.external_list_label_key || 'label';
  form.externalListValueKey = attr.external_list_value_key || 'value';
  form.externalListCacheTtl = attr.external_list_cache_ttl || 300;
  form.apiSearchable = !!attr.api_searchable;
  form.apiAuthType = attr.api_auth_type || 'none';
  form.externalListHeadersRaw = attr.external_list_headers
    ? JSON.stringify(attr.external_list_headers, null, 2)
    : '';
};

onMounted(async () => {
  if (!store.getters['attributes/getAttributes']?.length) {
    await store.dispatch('attributes/get');
  }
  hydrateFromStore();
});

watch(
  () => form.displayName,
  val => {
    if (mode.value === 'create') {
      form.attributeKey = convertToAttributeSlug(val || '');
    }
  }
);

const parsedHeaders = () => {
  if (!form.externalListHeadersRaw.trim()) return {};
  try {
    return JSON.parse(form.externalListHeadersRaw);
  } catch (_) {
    return null;
  }
};

const buildPayload = () => {
  const headers = parsedHeaders();
  return {
    attribute_display_name: form.displayName,
    attribute_description: form.description,
    attribute_key: form.attributeKey,
    attribute_model: form.attributeModel,
    attribute_display_type: form.attributeType,
    attribute_values:
      isList.value && !form.externalListEnabled ? form.values : [],
    regex_pattern: isText.value && form.regexEnabled ? form.regexPattern : null,
    regex_cue: isText.value && form.regexEnabled ? form.regexCue : null,
    is_required: form.isRequired,
    min_value: isNumber.value ? form.minValue : null,
    max_value: isNumber.value ? form.maxValue : null,
    min_length: isText.value ? form.minLength : null,
    max_length: isText.value ? form.maxLength : null,
    api_validator_url:
      canValidateViaApi.value && form.apiValidatorEnabled
        ? form.apiValidatorUrl
        : null,
    external_list_url:
      isList.value && form.externalListEnabled ? form.externalListUrl : null,
    external_list_label_key:
      isList.value && form.externalListEnabled
        ? form.externalListLabelKey
        : null,
    external_list_value_key:
      isList.value && form.externalListEnabled
        ? form.externalListValueKey
        : null,
    external_list_cache_ttl:
      isList.value && form.externalListEnabled
        ? form.externalListCacheTtl
        : null,
    api_searchable:
      isList.value && form.externalListEnabled ? form.apiSearchable : false,
    api_auth_type: usesApi.value ? form.apiAuthType : 'none',
    api_auth_token:
      usesApi.value && form.apiAuthType === 'bearer' ? form.apiAuthToken : null,
    external_list_headers: headers || {},
  };
};

const validateForm = () => {
  if (!form.displayName.trim()) {
    useAlert(t('ATTRIBUTES_MGMT.ADD.FORM.NAME.ERROR'));
    return false;
  }
  if (!form.attributeKey.trim() || form.attributeKey.includes(' ')) {
    useAlert(t('ATTRIBUTES_MGMT.ADD.FORM.KEY.ERROR'));
    return false;
  }
  if (isList.value && !form.externalListEnabled && form.values.length === 0) {
    useAlert(t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.ERROR'));
    return false;
  }
  if (form.externalListEnabled && !form.externalListUrl.trim()) {
    useAlert(t('ATTRIBUTES_MGMT.FORM_DIALOG.API_URL_REQUIRED'));
    return false;
  }
  if (form.apiValidatorEnabled && !form.apiValidatorUrl.trim()) {
    useAlert(t('ATTRIBUTES_MGMT.FORM_DIALOG.API_URL_REQUIRED'));
    return false;
  }
  if (parsedHeaders() === null) {
    useAlert(t('ATTRIBUTES_MGMT.FORM_DIALOG.HEADERS_INVALID'));
    return false;
  }
  return true;
};

const goBack = () => {
  router.push({ name: 'attributes_list', params: route.params });
};

const submit = async () => {
  if (!validateForm()) return;
  isSubmitting.value = true;
  try {
    const payload = buildPayload();
    if (mode.value === 'edit') {
      await store.dispatch('attributes/update', {
        id: props.attributeId,
        ...payload,
      });
      useAlert(t('ATTRIBUTES_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('attributes/create', payload);
      useAlert(t('ATTRIBUTES_MGMT.ADD.API.SUCCESS_MESSAGE'));
    }
    goBack();
  } catch (e) {
    useAlert(e?.message || t('ATTRIBUTES_MGMT.ADD.API.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};

const selectType = id => {
  form.attributeType = id;
};
const onValuesUpdated = newValues => {
  form.values = newValues;
};
</script>

<template>
  <SettingsLayout class="w-full max-w-2xl ltr:mr-auto rtl:ml-auto">
    <template #header>
      <div class="flex items-center gap-2 w-full justify-between mb-4 min-h-10">
        <Breadcrumb
          :items="breadcrumbItems"
          @click="handleBreadcrumbClick"
        />
      </div>
    </template>

    <template #body>
      <form class="pb-10" @submit.prevent="submit">
        <!-- Tabs no padrão PipelineEditor / FollowUpTemplateEditor -->
        <nav class="flex gap-1 border-b border-n-weak mb-6">
          <button
            v-for="tab in FORM_TABS"
            :key="tab.key"
            type="button"
            class="flex items-center gap-2 px-3 py-2 text-body-main border-b-2 transition"
            :class="activeTab === tab.key
              ? 'border-n-brand text-n-slate-12 font-medium'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
            @click="activeTab = tab.key"
          >
            <i :class="tab.icon" class="size-4" />
            {{ tab.label }}
          </button>
        </nav>

        <!-- Identificação -->
        <section v-show="activeTab === 'identity'">
          <h3 class="text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('ATTRIBUTES_MGMT.FORM_DIALOG.SECTION.IDENTITY') }}
          </h3>

          <div class="mt-4 space-y-4">
            <!-- Aplica-se a (radio cards) -->
            <div>
              <label
                class="text-xs font-medium text-n-slate-11 block mb-2"
              >
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.MODEL.LABEL') }}
              </label>
              <div class="grid grid-cols-1 xs:grid-cols-2 gap-4 w-full">
                <RadioCard
                  v-for="m in modelOptions"
                  :id="`model-${m.key}`"
                  :key="m.id"
                  :label="m.label"
                  :description="m.description"
                  :is-active="form.attributeModel === m.id"
                  @select="form.attributeModel = m.id"
                />
              </div>
            </div>

            <!-- Nome para exibição -->
            <div class="flex flex-col gap-1.5">
              <label
                class="text-sm font-medium text-n-slate-12"
                for="attr-display-name"
              >
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.NAME.LABEL') }}
              </label>
              <input
                id="attr-display-name"
                v-model="form.displayName"
                type="text"
                :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
                class="px-3 py-2 w-full text-sm rounded-lg border border-n-weak bg-n-alpha-1 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              />
            </div>

            <!-- Chave -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12" for="attr-key">
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.KEY.LABEL') }}
              </label>
              <input
                id="attr-key"
                v-model="form.attributeKey"
                :readonly="mode === 'edit'"
                type="text"
                class="px-3 py-2 w-full font-mono text-sm rounded-lg border border-n-weak bg-n-alpha-1 focus:outline-none focus:ring-2 focus:ring-n-brand/40 read-only:opacity-60"
              />
            </div>

            <!-- Descrição -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12" for="attr-desc">
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.LABEL') }}
              </label>
              <textarea
                id="attr-desc"
                v-model="form.description"
                rows="2"
                :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.DESC.PLACEHOLDER')"
                class="px-3 py-2 w-full text-sm rounded-lg border resize-y border-n-weak bg-n-alpha-1 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              />
            </div>
          </div>
        </section>

        <!-- Tipo do campo -->
        <section v-show="activeTab === 'type'">
          <h3 class="text-sm font-medium text-n-slate-12 mb-1">
            {{ $t('ATTRIBUTES_MGMT.FORM_DIALOG.SECTION.TYPE') }}
          </h3>

          <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <button
              v-for="card in TYPE_CARDS"
              :key="card.id"
              type="button"
              class="flex flex-col items-start gap-1 p-4 rounded-xl outline outline-1 transition-all duration-200 text-left bg-n-solid-1"
              :class="
                form.attributeType === card.id
                  ? 'outline-n-blue-9'
                  : 'outline-n-weak hover:outline-n-strong'
              "
              @click="selectType(card.id)"
            >
              <i
                class="size-5"
                :class="[
                  card.icon,
                  form.attributeType === card.id
                    ? 'text-n-brand'
                    : 'text-n-slate-11',
                ]"
              />
              <span class="text-sm font-semibold text-n-slate-12 mt-1">{{
                card.label
              }}</span>
              <span class="text-[11px] text-n-slate-10">{{ card.hint }}</span>
            </button>
          </div>
        </section>

        <!-- Validação - fica junto da aba "Tipo & Validação" -->
        <template v-if="!isCheckbox">
          <hr v-show="activeTab === 'type'" class="border-n-weak my-6" />

          <section v-show="activeTab === 'type'">
            <h3 class="text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('ATTRIBUTES_MGMT.FORM_DIALOG.SECTION.VALIDATION') }}
            </h3>

            <div class="mt-4 space-y-3">
              <label
                class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
              >
                <input v-model="form.isRequired" type="checkbox" />
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.VALIDATION.REQUIRED.LABEL') }}
              </label>

              <div v-if="isNumber" class="grid grid-cols-2 gap-3 max-w-md">
                <div>
                  <label class="text-xs text-n-slate-11 block mb-1.5">
                    {{ $t('ATTRIBUTES_MGMT.ADD.FORM.VALIDATION.MIN_VALUE') }}
                  </label>
                  <input
                    v-model.number="form.minValue"
                    type="number"
                    class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-1"
                  />
                </div>
                <div>
                  <label class="text-xs text-n-slate-11 block mb-1.5">
                    {{ $t('ATTRIBUTES_MGMT.ADD.FORM.VALIDATION.MAX_VALUE') }}
                  </label>
                  <input
                    v-model.number="form.maxValue"
                    type="number"
                    class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-1"
                  />
                </div>
              </div>

              <div v-if="isText" class="grid grid-cols-2 gap-3 max-w-md">
                <div>
                  <label class="text-xs text-n-slate-11 block mb-1.5">
                    {{ $t('ATTRIBUTES_MGMT.ADD.FORM.VALIDATION.MIN_LENGTH') }}
                  </label>
                  <input
                    v-model.number="form.minLength"
                    type="number"
                    class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-1"
                  />
                </div>
                <div>
                  <label class="text-xs text-n-slate-11 block mb-1.5">
                    {{ $t('ATTRIBUTES_MGMT.ADD.FORM.VALIDATION.MAX_LENGTH') }}
                  </label>
                  <input
                    v-model.number="form.maxLength"
                    type="number"
                    class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-1"
                  />
                </div>
              </div>

              <div v-if="isText" class="pt-1">
                <label
                  class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
                >
                  <input v-model="form.regexEnabled" type="checkbox" />
                  {{ $t('ATTRIBUTES_MGMT.ADD.FORM.ENABLE_REGEX.LABEL') }}
                </label>
                <div v-if="form.regexEnabled" class="space-y-2 mt-2 max-w-md">
                  <input
                    v-model="form.regexPattern"
                    type="text"
                    :placeholder="
                      $t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.PLACEHOLDER')
                    "
                    class="w-full px-3 py-2 text-sm font-mono rounded-md border border-n-weak bg-n-alpha-1"
                  />
                  <input
                    v-model="form.regexCue"
                    type="text"
                    :placeholder="
                      $t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.PLACEHOLDER')
                    "
                    class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-1"
                  />
                </div>
              </div>

              <div v-if="isList && !form.externalListEnabled" class="pt-1">
                <label class="text-xs text-n-slate-11 block mb-1.5">
                  {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.LABEL') }}
                </label>
                <div class="rounded-md border border-n-weak px-3 py-2">
                  <TagInput
                    :model-value="form.values"
                    :placeholder="
                      $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.PLACEHOLDER')
                    "
                    allow-create
                    @update:model-value="onValuesUpdated"
                  />
                </div>
              </div>
            </div>
          </section>
        </template>

        <!-- Integração API -->
        <template v-if="canValidateViaApi || isList">
          <section v-show="activeTab === 'api'">
            <h3 class="text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('ATTRIBUTES_MGMT.FORM_DIALOG.SECTION.API') }}
            </h3>

            <div class="mt-4 space-y-3">
              <div v-if="canValidateViaApi && !isList" class="space-y-2">
                <label
                  class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
                >
                  <input v-model="form.apiValidatorEnabled" type="checkbox" />
                  {{ $t('ATTRIBUTES_MGMT.FORM_DIALOG.API.VALIDATE_VALUE') }}
                </label>
                <template v-if="form.apiValidatorEnabled">
                  <input
                    v-model="form.apiValidatorUrl"
                    type="url"
                    :placeholder="
                      $t(
                        'ATTRIBUTES_MGMT.FORM_DIALOG.API.VALIDATE_URL_PLACEHOLDER'
                      )
                    "
                    class="w-full mt-1 px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-1"
                  />
                  <p class="text-xs leading-relaxed text-n-slate-10 mt-1">
                    {{ $t('ATTRIBUTES_MGMT.FORM_DIALOG.API.VALIDATE_HINT') }}
                  </p>
                </template>
              </div>

              <div v-if="isList" class="space-y-2">
                <label
                  class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
                >
                  <input v-model="form.externalListEnabled" type="checkbox" />
                  {{ $t('ATTRIBUTES_MGMT.FORM_DIALOG.API.LOAD_OPTIONS') }}
                </label>
                <template v-if="form.externalListEnabled">
                  <input
                    v-model="form.externalListUrl"
                    type="url"
                    :placeholder="
                      $t('ATTRIBUTES_MGMT.FORM_DIALOG.API.LIST_URL_PLACEHOLDER')
                    "
                    class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-1"
                  />
                  <div class="grid grid-cols-2 gap-2">
                    <input
                      v-model="form.externalListLabelKey"
                      type="text"
                      :placeholder="
                        $t('ATTRIBUTES_MGMT.ADD.FORM.EXTERNAL_LIST.LABEL_KEY')
                      "
                      class="px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-1"
                    />
                    <input
                      v-model="form.externalListValueKey"
                      type="text"
                      :placeholder="
                        $t('ATTRIBUTES_MGMT.ADD.FORM.EXTERNAL_LIST.VALUE_KEY')
                      "
                      class="px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-1"
                    />
                  </div>
                  <label
                    class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
                  >
                    <input v-model="form.apiSearchable" type="checkbox" />
                    {{ $t('ATTRIBUTES_MGMT.FORM_DIALOG.API.SEARCHABLE') }}
                  </label>
                  <input
                    v-model.number="form.externalListCacheTtl"
                    type="number"
                    min="0"
                    :placeholder="
                      $t('ATTRIBUTES_MGMT.ADD.FORM.EXTERNAL_LIST.CACHE_TTL')
                    "
                    class="w-full px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-1"
                  />
                </template>
              </div>

              <div
                v-if="usesApi"
                class="mt-4 pt-5 border-t border-n-weak space-y-3"
              >
                <label
                  class="text-xs font-semibold uppercase tracking-wide text-n-slate-11 block"
                >
                  {{ $t('ATTRIBUTES_MGMT.FORM_DIALOG.API.AUTH') }}
                </label>
                <div class="grid grid-cols-3 gap-2 max-w-md">
                  <button
                    v-for="auth in ['none', 'bearer', 'header']"
                    :key="auth"
                    type="button"
                    class="px-3 py-2 text-xs rounded-md border transition-colors"
                    :class="
                      form.apiAuthType === auth
                        ? 'border-n-brand bg-n-brand/10 text-n-brand'
                        : 'border-n-weak bg-n-alpha-1 text-n-slate-11'
                    "
                    @click="form.apiAuthType = auth"
                  >
                    {{
                      $t(
                        `ATTRIBUTES_MGMT.FORM_DIALOG.API.AUTH_${auth.toUpperCase()}`
                      )
                    }}
                  </button>
                </div>
                <input
                  v-if="form.apiAuthType === 'bearer'"
                  v-model="form.apiAuthToken"
                  type="password"
                  :placeholder="
                    $t('ATTRIBUTES_MGMT.FORM_DIALOG.API.BEARER_PLACEHOLDER')
                  "
                  class="w-full px-3 py-2 text-sm font-mono rounded-md border border-n-weak bg-n-alpha-1"
                />
                <textarea
                  v-if="form.apiAuthType === 'header'"
                  v-model="form.externalListHeadersRaw"
                  rows="3"
                  :placeholder="
                    $t('ATTRIBUTES_MGMT.FORM_DIALOG.API.HEADERS_PLACEHOLDER')
                  "
                  class="w-full px-3 py-2 text-xs font-mono rounded-md border border-n-weak bg-n-alpha-1"
                />
              </div>
            </div>
          </section>
        </template>

        <!-- Ações no rodapé -->
        <div class="flex items-center gap-2 mt-8">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="
              mode === 'edit'
                ? $t('ATTRIBUTES_MGMT.EDIT.UPDATE_BUTTON_TEXT')
                : $t('ATTRIBUTES_MGMT.ADD.SUBMIT')
            "
            @click.prevent="submit"
          />
          <NextButton
            faded
            slate
            type="button"
            :label="$t('ATTRIBUTES_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click="goBack"
          />
        </div>
      </form>
    </template>
  </SettingsLayout>
</template>
